import { useSyncExternalStore, createContext, useContext } from 'react';

import { useCustomContext } from './useCustomContext';

export interface FeedbackWidgetState {
  event_uuid: string;
  question_type: string;
}

type Listener = () => void;

export class HostStore<T> {
  private listeners: Set<Listener> = new Set();
  private value: T | null;

  constructor(initialValue: T | null) {
    this.value = initialValue;
  }

  getValue(): T | null {
    return this.value;
  }

  setValue(newValue: T): void {
    if (Object.is(this.value, newValue)) return;
    this.value = newValue;
    this.notify();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    this.listeners.forEach(listener => listener());
  }
}

export function useStore<T>(storeHost: HostStore<T>) {
  return useSyncExternalStore(
    storeHost.subscribe.bind(storeHost),
    storeHost.getValue.bind(storeHost),
  );
}

export function useFeedbackWidgetStore() {
  const { feedbackWidget } = useCustomContext();
  const { store } = feedbackWidget;
  return useStore(store as HostStore<FeedbackWidgetState>);
}

export const feedbackWidgetStore = new HostStore<FeedbackWidgetState>(null);
export const FeedbackWidgetContext =
  createContext<HostStore<FeedbackWidgetState> | null>(null);
export const useFeedbackWidgetContext =
  (): HostStore<FeedbackWidgetState> | null => {
    return useContext(FeedbackWidgetContext);
  };
